import { useState } from 'react';
import { Button, Field, I, Input, Pill, Row, Select, Stack, Textarea } from '@leanin/ui';
import { REGIONS, REGION_LABELS, type Region } from '@leanin/shared/region';

// The editable profile fields for a Network — name, tagline, region, mission,
// description, welcome, join instructions, tags, and social links. Shared by
// the admin Overview edit surface (NetworkOverviewSection) and the create
// flow (/networks/create) so the two never drift. Image upload, visibility,
// and the save/publish lifecycle are owned by each consumer; this component
// is a controlled view over the text content only.

export type NetworkSocialDraft = {
  website: string;
  linkedin: string;
  instagram: string;
  facebook: string;
  twitter: string;
  email: string;
};

export type NetworkProfileDraft = {
  name: string;
  tagline: string;
  region: Region;
  mission: string;
  description: string;
  welcomeMessage: string;
  joinInstructions: string;
  tags: string[];
  social: NetworkSocialDraft;
};

export const EMPTY_SOCIAL_DRAFT: NetworkSocialDraft = {
  website: '',
  linkedin: '',
  instagram: '',
  facebook: '',
  twitter: '',
  email: '',
};

type Props = {
  readonly value: NetworkProfileDraft;
  readonly onChange: (patch: Partial<NetworkProfileDraft>) => void;
};

export function NetworkProfileFields({ value, onChange }: Props) {
  const [tagInput, setTagInput] = useState('');

  const setSocial = (patch: Partial<NetworkSocialDraft>) =>
    onChange({ social: { ...value.social, ...patch } });

  const addTag = () => {
    const t = tagInput.trim();
    if (t.length > 0 && !value.tags.includes(t)) {
      onChange({ tags: [...value.tags, t] });
    }
    setTagInput('');
  };

  const removeTag = (tag: string) =>
    onChange({ tags: value.tags.filter((t) => t !== tag) });

  return (
    <>
      <Stack p="lg" gap="lg" style={{ borderBottom: '1px solid var(--border)' }}>
        <Row gap="md" wrap>
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Field label="Name" required>
              <Input
                value={value.name}
                onChange={(e) => onChange({ name: e.target.value })}
                maxLength={120}
              />
            </Field>
          </Stack>
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Field label="Tagline">
              <Input
                value={value.tagline}
                onChange={(e) => onChange({ tagline: e.target.value })}
                placeholder="A short, memorable line"
                maxLength={240}
              />
            </Field>
          </Stack>
        </Row>
        <Row gap="md" wrap align="flex-start">
          <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 320 }}>
            <Field label="Region" description="Which region this Network serves.">
              <Select
                value={value.region}
                options={REGIONS.map((r) => ({
                  value: r,
                  label: REGION_LABELS[r],
                }))}
                onChange={(e) => onChange({ region: e.target.value as Region })}
              />
            </Field>
          </Stack>
        </Row>
      </Stack>

      <Stack p="xl" gap="lg">
        <Field label="Mission">
          <Textarea
            value={value.mission}
            onChange={(e) => onChange({ mission: e.target.value })}
            rows={2}
            placeholder="Why this Network exists"
            maxLength={1000}
          />
        </Field>

        <Field label="Description (What we do)">
          <Textarea
            value={value.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={5}
            maxLength={2000}
          />
        </Field>

        <Field
          label="Welcome"
          description="Surfaces on the public Overview tab as a welcome card."
        >
          <Textarea
            value={value.welcomeMessage}
            onChange={(e) => onChange({ welcomeMessage: e.target.value })}
            rows={6}
            maxLength={4000}
            placeholder="e.g. We founded Lean In Network Italy in 2015 because…"
          />
        </Field>

        <Field
          label="How to join this Network"
          description="Surfaces on the public Overview tab. Use it to set expectations before someone requests to join (eligibility, intro form, response time)."
        >
          <Textarea
            value={value.joinInstructions}
            onChange={(e) => onChange({ joinInstructions: e.target.value })}
            rows={4}
            maxLength={4000}
            placeholder="e.g. Open to women in product across APAC. Request to join — leaders review weekly."
          />
        </Field>

        <Field label="Tags">
          <Stack gap="sm">
            <Row gap="sm" align="center">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add a tag…"
                style={{ maxWidth: 240 }}
              />
              <Button
                variant="ghost"
                size="sm"
                icon={I.plus}
                onClick={addTag}
                disabled={tagInput.trim().length === 0}
              >
                Add
              </Button>
            </Row>
            {value.tags.length > 0 ? (
              <Row gap="sm" wrap align="center">
                {value.tags.map((tag) => (
                  <Pill
                    key={tag}
                    tone="neutral"
                    onRemove={() => removeTag(tag)}
                    removeAriaLabel={`Remove tag ${tag}`}
                  >
                    {tag}
                  </Pill>
                ))}
              </Row>
            ) : null}
          </Stack>
        </Field>

        <Field
          label="Website & social links"
          description="Shown on the Network overview. Leave blank to hide a link."
        >
          <Stack gap="sm">
            <Row gap="md" wrap>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="Website">
                  <Input
                    value={value.social.website}
                    onChange={(e) => setSocial({ website: e.target.value })}
                    placeholder="https://example.org"
                  />
                </Field>
              </Stack>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="LinkedIn">
                  <Input
                    value={value.social.linkedin}
                    onChange={(e) => setSocial({ linkedin: e.target.value })}
                    placeholder="company-handle or full URL"
                  />
                </Field>
              </Stack>
            </Row>
            <Row gap="md" wrap>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="Instagram">
                  <Input
                    value={value.social.instagram}
                    onChange={(e) => setSocial({ instagram: e.target.value })}
                    placeholder="@handle"
                  />
                </Field>
              </Stack>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="Facebook">
                  <Input
                    value={value.social.facebook}
                    onChange={(e) => setSocial({ facebook: e.target.value })}
                    placeholder="page name or full URL"
                  />
                </Field>
              </Stack>
            </Row>
            <Row gap="md" wrap>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="X">
                  <Input
                    value={value.social.twitter}
                    onChange={(e) => setSocial({ twitter: e.target.value })}
                    placeholder="@handle or full URL"
                  />
                </Field>
              </Stack>
              <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                <Field label="Contact email">
                  <Input
                    type="email"
                    value={value.social.email}
                    onChange={(e) => setSocial({ email: e.target.value })}
                    placeholder="team@example.org"
                  />
                </Field>
              </Stack>
            </Row>
          </Stack>
        </Field>
      </Stack>
    </>
  );
}

// Maps a draft's social block to the strict, blank-stripped object the server
// accepts (SocialLinksSchema rejects empty strings). Shared by both consumers.
export function socialDraftToPayload(
  social: NetworkSocialDraft,
): Partial<Record<keyof NetworkSocialDraft, string>> {
  const out: Partial<Record<keyof NetworkSocialDraft, string>> = {};
  if (social.website.trim()) out.website = social.website.trim();
  if (social.linkedin.trim()) out.linkedin = social.linkedin.trim();
  if (social.instagram.trim()) out.instagram = social.instagram.trim();
  if (social.facebook.trim()) out.facebook = social.facebook.trim();
  if (social.twitter.trim()) out.twitter = social.twitter.trim();
  if (social.email.trim()) out.email = social.email.trim();
  return out;
}
